import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Shuffle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { ChessLogo } from "@/components/ChessLogo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function AuthModal({ open, tab = "register", onOpenChange }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(tab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [alias, setAlias] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setMode(tab);
  }, [open, tab]);

  const rollAlias = async () => {
    try {
      const { data } = await api.get("/auth/generate-alias");
      setAlias(data.alias);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (open && mode === "register" && !alias) rollAlias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        await register(email, password, alias);
        toast.success(`Welcome, ${alias}! You received 1 Dimagi coin.`);
      } else {
        const u = await login(email, password);
        toast.success(`Welcome back, ${u.alias}`);
      }
      onOpenChange(false);
      setEmail(""); setPassword(""); setAlias("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-modal" className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <ChessLogo size={40} />
            <div>
              <DialogTitle className="font-display">Dimagi National Zanata Dal</DialogTitle>
              <p className="text-xs text-muted-foreground">Issue-based politics. Real change.</p>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="mt-2">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger data-testid="auth-tab-register" value="register">Join</TabsTrigger>
            <TabsTrigger data-testid="auth-tab-login" value="login">Log in</TabsTrigger>
          </TabsList>

          <form onSubmit={submit} className="space-y-4 mt-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label>Your pseudo identity</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="auth-alias-input"
                    value={alias}
                    onChange={(e) => setAlias(e.target.value)}
                    placeholder="Pick an alias"
                  />
                  <Button
                    data-testid="auth-alias-shuffle"
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={rollAlias}
                    title="Generate alias"
                  >
                    <Shuffle size={16} />
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Your real name stays private. Only this alias is shown publicly.
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
              />
            </div>
            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-saffron text-primary-foreground hover:bg-saffron/90 font-semibold"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : mode === "register" ? "Join the movement" : "Log in"}
            </Button>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
